import { ApiService } from '../../api/api-service';
import { Manufacturer } from '../../types';

const SERVICE_ROOT = '/manufacturer';

/**
 * Service for manufacturer-related API operations
 */
export class ManufacturerService extends ApiService {
  /**
   * Get all manufacturers (no pagination)
   * @returns Promise resolving to all manufacturers
   */
  async getAllManufacturers(): Promise<Manufacturer[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}`),
      'Error fetching all manufacturers'
    );
  }

  /**
   * Get a manufacturer by ID
   * @param id Manufacturer ID
   * @returns Promise resolving to the manufacturer data
   */
  async getManufacturerById(id: string): Promise<Manufacturer> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/${id}`),
      `Error fetching manufacturer with ID ${id}`
    );
  }

  /**
   * Create a new manufacturer
   * @param manufacturer Manufacturer data
   * @param logoFile
   * @returns Promise resolving to the created manufacturer
   */
  async createManufacturer(
    manufacturer: Omit<Manufacturer, 'id'>,
    logoFile?: File
  ): Promise<Manufacturer> {
    return this.handleApiCall(async () => {
      // Create a FormData object
      const formData = new FormData();

      // Add all manufacturer fields to the form data
      Object.entries(manufacturer).forEach(([key, value]) => {
        if (value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      if (logoFile) {
        formData.append('logo', logoFile);
      }

      return this.client.post(SERVICE_ROOT, formData, {
        headers: {
          // Remove the default Content-Type so the browser can set it with the boundary parameter
          'Content-Type': 'multipart/form-data',
        },
      });
    }, 'Error creating manufacturer');
  }

  /**
   * Update a manufacturer
   * @param id Manufacturer ID
   * @param manufacturer Manufacturer data to update
   * @param logoFile Optional logo file to update
   * @returns Promise resolving to the updated manufacturer
   */
  async updateManufacturer(
    id: string,
    manufacturer: Partial<Manufacturer>,
    logoFile?: File
  ): Promise<Manufacturer> {
    return this.handleApiCall(async () => {
      // Create a FormData object for multipart/form-data
      const formData = new FormData();

      // Add all manufacturer fields to the form data
      Object.entries(manufacturer).forEach(([key, value]) => {
        if (value !== undefined) {
          formData.append(key, value.toString());
        }
      });

      // Add the logo file if provided
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      return this.client.put(`${SERVICE_ROOT}/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }, `Error updating manufacturer with ID ${id}`);
  }

  /**
   * Delete a manufacturer
   * @param id Manufacturer ID
   * @returns Promise resolving when the manufacturer is deleted
   */
  async deleteManufacturer(id: string): Promise<void> {
    return this.handleApiCall(
      () => this.client.delete(`${SERVICE_ROOT}/${id}`),
      `Error deleting manufacturer with ID ${id}`
    );
  }
}
